import { useEffect, useMemo, useRef, useState } from "react";
import SubNav from "../../plan-template/SubNav";
import Tabs from "../../tabs/Tabs";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import StatusContent from "../../plan-download/StatusContent";
import BoxPlanOrganizationTemplate from "../../popups/BoxPlanOrganizationTemplate";
import AdditionalPrintSettings from "../../plan-download/AdditionalPrintSettings";
import Sidebar from "../../sidebar";
import useStatusContentHook from "../../../hooks/plan-download/use-status-content.hook";
import useTemplatePickerHook from "../../../hooks/plan-download/use-template-picker.hook";
import useAdditionalSettingsPrintHook from "../../../hooks/plan-download/use-additional-settings-print.hook";
import { getPresetSelection } from "../../../helper/formatter.helper";
import LoadingBox from "../../loading/LoadingBox";
import FilterDropdown from "../../utils/FilterDropdown";
import isEqual from "lodash.isequal";
import { cn } from "../../../lib/utils";
import { editTemplatePreset, getTemplatePresetDetail } from "../../../services/presets.service";
import useNotification from "../../../hooks/notification.hook";
import EmptyBox from "../../emptystate/EmptyBox";
import CollapsiblePlanContents from "../../plan-template/CollapsiblePlanContents";
import { useSessionStore } from "../../../store/session.store";

const defaultFiltersValue = {
	resellers: [],
	tags: [],
};

const PresetConfiguration = ({ preset, data, subNavRightContent, onSave }) => {
	const [tabContent, setTabContent] = useState("status");
	const [filters, setFilters] = useState(defaultFiltersValue);
	const [search, setSearch] = useState("");
	const [isUpdating, setIsUpdating] = useState(false);
	const [selectedOrganizations, setSelectedOrganizations] = useState([]);
	const [isGenerics, setIsGenerics] = useState(false);
	const initialState = useRef(null);
	const signalRef = useRef(new AbortController());
	const [isLoading, setIsLoading] = useState(false);
	const { user } = useSessionStore();

	const hasFilterActive = useMemo(
		() => JSON.stringify(defaultFiltersValue) !== JSON.stringify(filters),
		[filters]
	);

	const filteredOrganizations = useMemo(() => {
		const string = search.trim().toLowerCase();
		const resellerIds = preset?.owner?.id ? [preset.owner.id] : filters.resellers;

		return data.organizations.filter(
			(item) =>
				(resellerIds.length === 0 || resellerIds.includes(item.reseller_id)) &&
				(filters.tags.length === 0 ||
					filters.tags.some((tagId) => item.tags.some((t) => t.id === tagId))) &&
				(string === "" || item.name.toLowerCase().includes(string))
		);
	}, [data.organizations, filters.resellers, filters.tags, search, preset?.owner?.id]);

	const updateFilters = (partialFilter) => {
		setFilters((filters) => ({ ...filters, ...partialFilter }));
	};

	const { toggleAllStatus, toggleStatus, selectedStatus, setSelectedStatus } = useStatusContentHook(
		data.statuses
	);

	const { selectedTemplateConfiguration, setSelectedTemplateConfiguration } =
		useTemplatePickerHook();

	const { updateDeadlinesSettings, deadlinesSettings, updateApprovalsSettings, approvalsSettings } =
		useAdditionalSettingsPrintHook();

	const { helperErrorNotification, openNotification } = useNotification();

	const sortById = (arr) => [...arr].sort((a, b) => a.id - b.id);

	const normalizeState = (state) => ({
		...state,
		selectedStatus: [...state.selectedStatus].sort(),
		templateConfiguration: {
			chapters: [...state.templateConfiguration.chapters].sort(),
			contents: sortById(state.templateConfiguration.contents),
			goals: sortById(state.templateConfiguration.goals),
			reqs: sortById(state.templateConfiguration.reqs),
		},
		organization_ids: [...state.organization_ids].sort(),
	});

	/**
	 * Check if there are edits
	 * @type {boolean}
	 */
	const isDirty = useMemo(() => {
		const currentState = normalizeState({
			deadlines: deadlinesSettings,
			approvals: { included: approvalsSettings.included },
			selectedStatus,
			templateConfiguration: selectedTemplateConfiguration,
			isGenerics,
			organization_ids: selectedOrganizations,
		});

		const initial = initialState?.current ? normalizeState(initialState?.current || {}) : undefined;

		if (!initial) return false;

		return !isEqual(currentState, initial);
	}, [
		deadlinesSettings,
		approvalsSettings.included,
		selectedStatus,
		selectedTemplateConfiguration,
		isGenerics,
		selectedOrganizations,
	]);

	/**
	 * Init data
	 */
	const initDataHandler = (presetData) => {
		const emptyFill = { chapters: [], contents: [], goals: [], reqs: [] };
		const state = {
			deadlines: {
				included: !!presetData?.options?.deadlines,
				from: presetData?.options?.deadline_date_from || null,
				to: presetData?.options?.deadline_date_to || null,
			},
			approvals: { included: !presetData?.options?.hide_approval_section },
			selectedStatus: presetData?.options?.organizationRequirementsStatuses ?? [],
			templateConfiguration: presetData
				? getPresetSelection(
						data?.content,
						presetData.options.templateRequirements,
						presetData.options.templateChapterContents
					)
				: emptyFill,
			isGenerics: !presetData?.organization_ids?.length,
			organization_ids: presetData?.organization_ids || [],
		};

		initialState.current = JSON.parse(JSON.stringify(state));
		updateDeadlinesSettings(state.deadlines);
		updateApprovalsSettings(state.approvals);
		setSelectedStatus(state.selectedStatus);
		setSelectedTemplateConfiguration(state.templateConfiguration);
		setIsGenerics(state.isGenerics);
		setSelectedOrganizations(state.organization_ids);
	};

	useEffect(() => {
		getPresetDetail();
	}, [preset?.id]);

	const updatePresetHandler = async () => {
		if (isUpdating) return;

		if (!isGenerics && !selectedOrganizations.length) {
			openNotification({
				text: "Seleziona almeno un ente per continuare!",
				status: "error",
			});
			return;
		}

		setIsUpdating(true);

		const templateChapters = selectedTemplateConfiguration.chapters;

		const templateChapterContents = selectedTemplateConfiguration.contents.map(
			(content) => content.id
		);

		const templateGoals = selectedTemplateConfiguration.goals.map((goal) => goal.id);

		const templateRequirements = selectedTemplateConfiguration.reqs.map((r) => r.id);

		const body = {
			options: {
				organizationRequirementsStatuses: selectedStatus,
				templateChapters,
				templateChapterContents,
				templateGoals,
				templateRequirements,
				deadlines: deadlinesSettings.included,
				deadline_date_from: deadlinesSettings.from,
				deadline_date_to: deadlinesSettings.to,
				hide_approval_section: !approvalsSettings.included,
				show_only_last_approval: approvalsSettings.included,
			},
			organization_ids: isGenerics ? [] : selectedOrganizations,
		};

		await editTemplatePreset(
			{ ...body, id: preset?.id, name: preset?.name },
			(res) => {
				initDataHandler(res.data);
				openNotification({
					text: "Configurazione modificata con successo!",
					status: "success",
				});
				onSave?.(res.data);
			},
			(err) => {
				helperErrorNotification(err);
			}
		);

		setIsUpdating(false);
	};

	const handleOrganizationSelect = (id) => {
		if (!canEdit) return;
		setSelectedOrganizations((prev) =>
			prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
		);
	};

	const handleGenericsToggle = () => {
		if (!canEdit) return;
		setIsGenerics((prev) => !prev);
		setSelectedOrganizations([]);
	};

	const handleSelectAll = (e) => {
		if (!canEdit) return;
		if (e.target.checked) {
			setSelectedOrganizations((prev) => [
				...new Set([...prev, ...filteredOrganizations.map((x) => x.id)]),
			]);
		} else {
			const filteredIds = new Set(filteredOrganizations.map((x) => x.id));
			setSelectedOrganizations((prev) => prev.filter((id) => !filteredIds.has(id)));
		}
	};

	const allSelected = filteredOrganizations.every((x) => selectedOrganizations.includes(x.id));
	const someSelected = filteredOrganizations.some((x) => selectedOrganizations.includes(x.id));

	/**
	 * Get presets list
	 * @returns {Promise<void>}
	 */
	const getPresetDetail = async () => {
		signalRef.current = new AbortController();

		setIsLoading(true);

		await getTemplatePresetDetail(
			{
				params: { id: preset.id },
				config: {
					signal: signalRef.current.signal,
				},
			},
			(res) => {
				initDataHandler(res.data);
			},
			() => {}
		);

		setIsLoading(false);
	};

	const filteredViewData = useMemo(() => {
		if (!data?.content || !selectedTemplateConfiguration) return [];

		const enabledChapterIds = new Set(selectedTemplateConfiguration.chapters);
		const enabledContentIds = new Set(selectedTemplateConfiguration.contents.map((c) => c.id));
		const enabledGoalIds = new Set(selectedTemplateConfiguration.goals.map((g) => g.id ?? g));
		const enabledReqIds = new Set(selectedTemplateConfiguration.reqs.map((r) => r.id ?? r));

		return data.content
			.filter((chapter) => enabledChapterIds.has(chapter.id))
			.map((chapter) => ({
				...chapter,
				contents: chapter.contents
					.filter((item) => {
						if (item.content_type === "chapter_content") return enabledContentIds.has(item.id);
						if (item.content_type === "chapter_goal") return enabledGoalIds.has(item.id);
						return false;
					})
					.map((item) => {
						if (item.content_type === "chapter_goal") {
							return {
								...item,
								requirements: (item.requirements ?? []).filter((r) => enabledReqIds.has(r.id)),
							};
						}
						return item;
					}),
			}));
	}, [data, selectedTemplateConfiguration]);

	const canEdit = useMemo(() => !!preset?.policies?.update, [preset?.policies?.update]);

	return (
		<>
			<SubNav
				title={preset.name}
				wrapperClassName="border-b border-dark-10"
				TemplateRightContent={() => (
					<div className="main_comp_actions_group">
						{subNavRightContent}
						{preset?.policies?.update && (
							<button
								type="button"
								className={cn(
									"button fill success small",
									!isDirty && "disabled",
									isUpdating && "loading"
								)}
								disabled={!isDirty}
								onClick={updatePresetHandler}
							>
								<div className="load_content">Salva</div>
								{isUpdating && <div className="main_loader_icon fade_in"></div>}
							</button>
						)}
					</div>
				)}
			/>

			{isLoading ? (
				<div className="wrap_page_sidebar">
					<LoadingBox blocked={true} />
				</div>
			) : (
				<div className="wrap_page_sidebar max-h-full !p-0">
					<div className="flex flex-1">
						<div className="flex flex-col flex-1 w-0 overflow-hidden">
							<div className="px-6 flex flex-col flex-1 h-0 basis-0">
								<div className="pb-6 border-b border-dark-10 pt-7">
									<div className="uppercase text-14 font-bold text-dark-50 mb-4">
										Dettagli della configurazione
									</div>

									<Tabs
										size="small"
										selected={tabContent}
										onChange={setTabContent}
										options={[
											{ label: "Stati linee d'azione", value: "status" },
											{ label: "Contenuto", value: "content" },
											{ label: "Impostazioni", value: "settings" },
										]}
									/>
								</div>

								<OverlayScrollbarsComponent className={"pt-4 -mx-6 px-6"} defer>
									<div className={cn("pb-7", isUpdating && "pointer-events-none opacity-70")}>
										{tabContent === "status" && (
											<StatusContent
												selectedStatues={selectedStatus}
												listStatuses={data.statuses}
												onToggleStatus={toggleStatus}
												onToggleAllStatus={toggleAllStatus}
												className="pb-0 !px-0"
												readOnly={!preset?.policies?.update}
											/>
										)}

										{tabContent === "content" &&
											(!preset?.policies?.update ? (
												<CollapsiblePlanContents chapters={filteredViewData} />
											) : (
												<BoxPlanOrganizationTemplate
													template={data.content}
													setSelectedItems={setSelectedTemplateConfiguration}
													selectedItems={selectedTemplateConfiguration}
													disableOverlayScrollbars={true}
													className="!h-full -ml-6"
												/>
											))}

										{tabContent === "settings" && (
											<AdditionalPrintSettings
												formClassName="!py-6 !px-0"
												className="px-0"
												deadlinesSettings={deadlinesSettings}
												updateDeadlinesSettings={updateDeadlinesSettings}
												approvalsSettings={approvalsSettings}
												updateApprovalSettings={updateApprovalsSettings}
												approvalDescription="Il preset consente di includere esclusivamente l’ultima adozione effettuata."
												readOnly={!preset?.policies?.update}
											/>
										)}
									</div>
								</OverlayScrollbarsComponent>
							</div>
						</div>

						<div className="w-[350px] xxl:w-[316px] flex-none flex flex-col">
							<Sidebar
								side="right"
								className="static max-w-full pt-0 bg-dark-5 flex-1 h-0 basis-0 border-l border-dark-10 z-10"
								hasFilterApplied={hasFilterActive}
							>
								<Sidebar.Header
									title="Enti"
									actions={
										selectedOrganizations.length > 0 && (
											<div className="text-12 text-dark-70">
												Selezionati: <b className="font-semibold">{selectedOrganizations.length}</b>
											</div>
										)
									}
								>
									<div className={cn("toggle_landing", !canEdit && "opacity-70")}>
										<input
											type="checkbox"
											id="prest-generic-type-toggle"
											className="landing"
											checked={isGenerics}
											onChange={handleGenericsToggle}
											disabled={!canEdit}
										/>
										<label htmlFor="prest-generic-type-toggle">
											<span className="flex items-center gap-2">
												Imposta come generico
												<span
													className="icon_16 default info"
													data-tooltip-id="tooltip_small_top"
													data-tooltip-offset={10}
													data-tooltip-content="Tutti gli enti avranno accesso alla configurazione"
												/>
											</span>
										</label>
									</div>

									{!isGenerics && (
										<>
											<div className="flex items-center gap-4">
												<div className="wrap_input_landing icon w-full">
													<input
														onChange={(e) => setSearch(e.target.value)}
														type="text"
														className="landing"
														placeholder="Ricerca..."
														value={search}
													/>
													<span className="icon_16 default search"></span>
												</div>

												{user?.role === "admin" && <Sidebar.FilterToggle />}
											</div>

											{user?.role === "admin" && (
												<Sidebar.FilterContent
													title="Filtri avanzati"
													onReset={() => updateFilters(defaultFiltersValue)}
												>
													{!preset?.owner?.id && (
														<FilterDropdown
															options={data.resellers.map((el) => ({
																label: el.name,
																value: el.id,
															}))}
															activeSelected={filters.resellers}
															onApply={(items) => updateFilters({ resellers: items })}
															toggleLabel="Rivenditori"
															searchFilter
															contentClassName="right-0"
															isLoading={data.isLoadingResellers}
														/>
													)}

													<FilterDropdown
														options={data.tags.map((el) => ({
															label: el.label,
															value: el.id,
														}))}
														activeSelected={filters.tags}
														onApply={(items) => updateFilters({ tags: items })}
														toggleLabel="Tag"
														searchFilter
														contentClassName="right-0"
														isLoading={data.isLoadingTags}
													/>
												</Sidebar.FilterContent>
											)}
										</>
									)}
								</Sidebar.Header>
								<Sidebar.List>
									{isGenerics ? (
										<div className="py-6">
											<EmptyBox
												title="Configurazione impostata come generica"
												description="Tutti i tuoi enti avranno accesso alla configurazione"
												icon="icon_75 default archive_entity"
											/>
										</div>
									) : data.isLoadingOrganization ? (
										<LoadingBox blocked="true" />
									) : filteredOrganizations.length ? (
										<div className="grid gap-5">
											<div className="pb-4 border-b border-dark-10 sticky top-0 bg-dark-5 z-10 -mt-6 pt-6">
												<div className="flex gap-3">
													<div
														className={cn(
															"check_landing small",
															someSelected && !allSelected && "partial",
															!canEdit && "opacity-70"
														)}
													>
														<input
															type="checkbox"
															checked={
																filteredOrganizations.length > 0 &&
																filteredOrganizations.every((x) =>
																	selectedOrganizations.includes(x.id)
																)
															}
															onChange={handleSelectAll}
															id="select-entities-all"
															disabled={!canEdit}
														/>
														<label htmlFor="select-entities-all" className="only_toggle">
															<span className="sr-only">Seleziona tutte le organizzazioni</span>
														</label>
													</div>
													Seleziona tutti
												</div>
											</div>

											<div className="divide-y divide-dark-10 rounded overflow-hidden ring-inset ring-1 ring-dark-10">
												{filteredOrganizations.map((organization) => (
													<div
														key={organization.id}
														className="bg-white flex items-center gap-4 px-3 py-1.5"
													>
														<div className={cn("check_landing small", !canEdit && "opacity-70")}>
															<input
																disabled={!canEdit}
																type="checkbox"
																checked={selectedOrganizations.includes(organization.id)}
																onChange={() => handleOrganizationSelect(organization.id)}
																id={`select-entity-${organization.id}`}
															/>
															<label
																htmlFor={`select-entity-${organization.id}`}
																className="only_toggle"
															>
																<span className="sr-only">Seleziona {organization.name}</span>
															</label>
														</div>
														{organization.logo_url ? (
															<img
																src={organization.logo_url}
																className="size-5 object-contain flex-none"
																alt=""
															/>
														) : (
															<div className="flex-none flex">
																<span className="icon_16 default entity opacity-40"></span>
															</div>
														)}
														{organization.name}
													</div>
												))}
											</div>
										</div>
									) : (
										<div className="py-6">
											<EmptyBox
												title={"Nessun risultato trovato"}
												icon={"icon_75 default archive_entity"}
											/>
										</div>
									)}
								</Sidebar.List>
							</Sidebar>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default PresetConfiguration;
